/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import { RESOURCE } from './ResourceType';
import { ResourceModel } from './Model';
import { IResourceProps } from './ResourceLoader';

/**
 * Common URI path to full webtop images
 * TODO: This paths should go to common location
 */
const PUBLIC_IMAGES = '/public/images/';
const PUBLIC_IMAGES_DEFAULT = PUBLIC_IMAGES + 'full_wt/';
const PUBLIC_IMAGES_CUSTOM = PUBLIC_IMAGES + 'customization/';
const PUBLIC_IMAGES_WEBTOP = PUBLIC_IMAGES + 'modern/webtop/';

const outdatedImages: string[] = [
    PUBLIC_IMAGES_DEFAULT + 'link.png',
    PUBLIC_IMAGES_DEFAULT + 'application.png',
    PUBLIC_IMAGES_DEFAULT + 'terminal_service.png',
    PUBLIC_IMAGES_DEFAULT + 'network_access.png',
];

enum LaunchStatus {
    INITIAL,
    IN_PROGRESS,
    ERROR,
    CONNECTED,
}

abstract class WebtopResourceModel extends ResourceModel {
    // static
    static readonly propNames = ['custom_img_path', ...ResourceModel.propNames];
    /**
     *  Overlay icons for indication of resource launch status
     */
    static statusOverlayIcons: Partial<Record<LaunchStatus, string>> = {
        [LaunchStatus.IN_PROGRESS]: PUBLIC_IMAGES_DEFAULT + 'waiting.gif',
        [LaunchStatus.ERROR]: PUBLIC_IMAGES_WEBTOP + 'overlay-icon-error.png',
    };
    /**
     *  Default icons for known resource types
     */
    static defaultIcons: Partial<Record<RESOURCE, string>> = {
        [RESOURCE.PORTAL_ACCESS]: PUBLIC_IMAGES_DEFAULT + 'icon-pa-url.png',
        [RESOURCE.WEBTOP_FOLDER]: PUBLIC_IMAGES_DEFAULT + 'icon-folder.png',
        [RESOURCE.WEBTOP_LINK]: PUBLIC_IMAGES_DEFAULT + 'icon-pa-url.png',
        [RESOURCE.APP_TUNNEL]: PUBLIC_IMAGES_DEFAULT + 'icon-na-at.png',
        [RESOURCE.REMOTE_DESKTOP]: PUBLIC_IMAGES_DEFAULT + 'icon-rd-ms.png',
        [RESOURCE.NETWORK_ACCESS]: PUBLIC_IMAGES_DEFAULT + 'icon-na-at.png',
        [RESOURCE.SAML]: PUBLIC_IMAGES_DEFAULT + 'icon-pa-url.png',
        [RESOURCE.WEBSSH]: PUBLIC_IMAGES_DEFAULT + 'icon-pa-url.png',
    };

    static getDefaultIcon(type: RESOURCE) {
        const icon = WebtopResourceModel.defaultIcons[type];
        return icon ? icon : '';
    }

    // instance
    public icon: string;
    public iconOverlay?: string;
    public lastError: any;

    private statusValue: LaunchStatus = LaunchStatus.INITIAL;

    constructor(id: string, resource: IResourceProps) {
        super(id, resource);
        if (resource.props.custom_img_path && outdatedImages.indexOf(resource.props.custom_img_path) === -1) {
            this.icon = resource.props.custom_img_path;
        }
    }

    get status() {
        return this.statusValue;
    }

    set status(newStatus: LaunchStatus) {
        this.iconOverlay = WebtopResourceModel.statusOverlayIcons[newStatus];
        this.statusValue = newStatus;
        if (newStatus === LaunchStatus.CONNECTED) {
            /* Clear lastError after successful connection */
            this.lastError = undefined;
        }
    }

    undefinedIconToDefault() {
        if (!this.icon) {
            this.icon = WebtopResourceModel.getDefaultIcon(this.type);
        }
    }

    getPropNames(): string[] {
        return WebtopResourceModel.propNames;
    }

    hasSearchPatternLow(searchPatternLow: string): boolean {
        return (
            this.description.toLocaleLowerCase().indexOf(searchPatternLow) !== -1 ||
            this.caption.toLocaleLowerCase().indexOf(searchPatternLow) !== -1
        );
    }
}

export {
    WebtopResourceModel as default,
    WebtopResourceModel,
    LaunchStatus,
    PUBLIC_IMAGES,
    PUBLIC_IMAGES_DEFAULT,
    PUBLIC_IMAGES_CUSTOM,
};
